/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { type ReactElement, type ReactNode } from 'react';
import { type CheckedState } from '@radix-ui/react-checkbox';

import { Checkbox } from '@/components/forms/checkbox';
import { cn } from '@/lib/utils';

interface CartLineItemAddOnRowProps {
    id: string;
    label: string;
    priceText?: string;
    checked?: CheckedState;
    defaultChecked?: CheckedState;
    disabled?: boolean;
    size?: 'default' | 'compact';
    className?: string;
    leadingContent?: ReactNode;
    onCheckedChange?: (checked: CheckedState) => void;
}

export default function CartLineItemAddOnRow({
    id,
    label,
    priceText,
    checked,
    defaultChecked,
    disabled = false,
    size = 'default',
    className,
    leadingContent,
    onCheckedChange,
}: CartLineItemAddOnRowProps): ReactElement {
    const compact = size === 'compact';

    return (
        <div
            className={cn(
                'flex w-full items-start gap-2 bg-secondary text-foreground',
                compact ? 'px-3 py-3' : 'px-3 py-3',
                className
            )}>
            <Checkbox
                id={id}
                checked={checked}
                defaultChecked={defaultChecked}
                disabled={disabled}
                onCheckedChange={onCheckedChange}
            />
            <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2 justify-between">
                    <div className="min-w-0 flex-1">
                        {leadingContent ? <div className="mb-1">{leadingContent}</div> : null}
                        <label
                            htmlFor={id}
                            className={cn(
                                'block cursor-pointer font-semibold',
                                compact ? 'text-xs md:text-sm leading-4' : 'text-sm leading-5'
                            )}>
                            {label}
                        </label>
                    </div>
                    {priceText ? (
                        <span
                            className={cn('shrink-0 text-right', compact ? 'text-xs leading-4' : 'text-sm leading-5')}>
                            {priceText}
                        </span>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
